import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useAuth } from "../../Context/useAuth";
import { activateDeviceAPI, deactivateDeviceAPI } from "../../Services/Http/ActivationService";
import { ValidationResult } from "../../Models/ValidationResult";
import { DeviceActivateRequest } from "../../Models/DeviceActivateRequest";
import { toast } from "react-toastify";
import { ApiError } from "../../Models/ApiError";
import { DeviceDeactivateRequest } from "../../Models/DeviceDeactivateRequest";
import { DeviceDeactivateResponse } from "../../Models/DeviceDeactivateResponse";
import { dataContext } from "../../Context/contextProvider";
import { DeviceActivateResponse } from "../../Models/DeviceActivateResponse";
import { CheckActivationResult } from "../../Models/CheckActivationResult";

interface ModalRegisterDeviceProps {
  modalOpen: boolean;
  passActiveDeviceNames: string[];
  passActiveDeviceCount: number;
  passMaxDeviceCount: number;
  onClose?: () => void;
  onRegistrationComplete?: (serverUrl: string) => void;
}
interface DeviceRegisterValidationErrors {
  deviceName?: ValidationResult;
}

const ModalRegisterDevice = ({ modalOpen, passActiveDeviceNames, passActiveDeviceCount, passMaxDeviceCount, onClose, onRegistrationComplete }: ModalRegisterDeviceProps) => {
  const { getDeviceId, initActivation } = useAuth();
  const [showDeviceRegistration, setShowDeviceRegistration] = useState<boolean>(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [errors, setErrors] = useState<DeviceRegisterValidationErrors>({});
  const [localDeviceName, setLocalDeviceName] = useState<string>("");
  const [activeDeviceNames, setActiveDeviceNames] = useState<string[]>(passActiveDeviceNames);
  const [activeDeviceCount, setActiveDeviceCount] = useState<number>(passActiveDeviceCount);
  const [maxDeviceCount, setMaxDeviceCount] = useState<number>(passMaxDeviceCount);
  const dataStore = useContext(dataContext);

  const completeRegistration = useCallback(
    (serverUrl: string) => {
      setShowDeviceRegistration(false);
      setSubmitDisabled(false);
      setErrors({});
      setLocalDeviceName("");
      setActiveDeviceNames([]);
      setActiveDeviceCount(0);
      setMaxDeviceCount(0);
      onRegistrationComplete?.(serverUrl);
    },
    [onRegistrationComplete],
  );
  useEffect(() => {
    setActiveDeviceNames([...passActiveDeviceNames]);
  }, [passActiveDeviceNames]);

  useEffect(() => {
    setActiveDeviceCount(passActiveDeviceCount);
  }, [passActiveDeviceCount]);

  useEffect(() => {
    setMaxDeviceCount(passMaxDeviceCount);
  }, [passMaxDeviceCount]);

  useEffect(() => {
    initActivation().then((result: CheckActivationResult) => {
      if (result.alreadyChecked || result.busy) {
        return; //do nothing
      } else if (result.deviceIdNotFound) {
        toast.error("Device ID not found. Please restart the application.");
        return;
      } else if (result.continueToCompleteRegistration) {
        completeRegistration(result.serverUrl || "");
      } else if (result.setShowDeviceRegistration) {
        setShowDeviceRegistration(true);
      }
    });
  }, [dataStore, dataStore.serverUrl, dataStore.licenseValidatedServerUrl, completeRegistration, getDeviceId, initActivation]);

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleActivateDevice();
    }
  };
  const handleActivateDevice = async () => {
    //check form validity
    const err: DeviceRegisterValidationErrors = {};
    let hasErrors: boolean = false;
    if (!localDeviceName) {
      err.deviceName = {
        message: "Device name is required",
        valid: false,
      };
      hasErrors = true;
    }
    setErrors(err);
    if (hasErrors) {
      return;
    }
    if (!dataStore.serverUrl) {
      toast.error("Server URL is not set");
      return;
    }
    setSubmitDisabled(true);
    setActiveDeviceNames([]);
    setActiveDeviceCount(0);
    setMaxDeviceCount(0);
    try {
      const deviceId = getDeviceId();
      if (!deviceId) {
        toast.error("Device ID not found. Please restart the application.");
        setSubmitDisabled(false);
        return;
      }
      const model: DeviceActivateRequest = {
        deviceId: deviceId,
        deviceName: localDeviceName,
        deviceInfo: navigator.userAgent,
        serverUrl: dataStore.serverUrl,
      };
      const resp: DeviceActivateResponse = await activateDeviceAPI(model);
      if (resp.success) {
        dataStore.permittedServers = resp.permittedServerUrls ? [...resp.permittedServerUrls] : [];
        toast.success("Device registered successfully");
        completeRegistration(dataStore.serverUrl);
      } else {
        toast.error(resp.errorMessage || "Device registration failed");
        if (resp.activeDeviceNames && resp.activeDeviceNames.length > 0) {
          setActiveDeviceNames(resp.activeDeviceNames);
          setActiveDeviceCount(resp.activeDeviceCount || 0);
          setMaxDeviceCount(resp.countLimit || 0);
        }
      }
    } catch (err) {
      const error = err as ApiError;
      if (error?.code?.toUpperCase() !== "ERR_NETWORK") {
        toast.error("Network error occurred while registering device");
      } else {
        toast.error(error.message || "An error occurred while registering device");
      }
    } finally {
      setSubmitDisabled(false);
    }
  };

  const deactivateDevice = async (deviceName: string) => {
    const confirmed = window.confirm(`Are you sure to deactivate ${deviceName} ?`);
    if (!confirmed) {
      return;
    }
    setSubmitDisabled(true);
    try {
      if (!dataStore.serverUrl) {
        toast.error("Server URL is not set");
        return;
      }
      const model: DeviceDeactivateRequest = {
        deviceName: deviceName,
        serverUrl: dataStore.serverUrl,
      };
      const resp: DeviceDeactivateResponse = await deactivateDeviceAPI(model);
      if (resp.success) {
        toast.success(`${deviceName} deactivated`);
        setActiveDeviceNames(resp.activeDeviceNames || []);
        setActiveDeviceCount(resp.activeDeviceCount || 0);
        setMaxDeviceCount(resp.countLimit || 0);
      } else {
        toast.error(resp.errorMessage || `Failed to deactivate "${deviceName}"`);
      }
    } catch (err) {
      const error = err as ApiError;
      toast.error(error.message || "An error occurred while deactivating device");
    } finally {
      setSubmitDisabled(false);
    }
  };

  return (
    <Modal show={modalOpen} backdrop="static" fullscreen={true} keyboard={false} centered size="xl">
      <Modal.Header>
        <Modal.Title>Device Registration</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {activeDeviceNames && activeDeviceNames.length > 0 && activeDeviceCount >= maxDeviceCount && (
          <>
            <h3>Current Active Devices</h3>
            <p>
              You have <b>{activeDeviceCount}</b> active devices out of a maximum of <b>{maxDeviceCount}</b> allowed.
            </p>
            <p>To register a new device, please deactivate an existing one or contact eSuite to increase your device limit.</p>
            <b>Select a device to deactivate</b>
            <div className="row mb-2">
              <div className="col-lg-4 offset-lg-4 col-md-6 offset-md-3 col-sm-8 offset-sm-2 col-12 mb-2 mt-4">
                <div className="d-grid gap-2 mt-2">
                  {activeDeviceNames.map((deviceName, index) => (
                    <button key={index} type="button" className="btn btn-outline-danger" onClick={() => deactivateDevice(deviceName)} disabled={submitDisabled}>
                      {deviceName}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
        {showDeviceRegistration && activeDeviceCount < maxDeviceCount && (
          <div className="row mb-2">
            <div className="col-lg-4 offset-lg-4 col-md-6 offset-md-3 col-sm-8 offset-sm-2 col-12 mb-2 mt-4">
              <div className="d-grid gap-2" onKeyDown={handleFormKeyDown}>
                <h5 className="mb-4">Please register this device</h5>
                <div>
                  <label className="mb-2" htmlFor="deviceName">
                    Device name
                  </label>
                  <div className="d-flex">
                    <input
                      name="deviceName"
                      id="deviceName"
                      type="text"
                      className={errors?.deviceName ? "form-control is-invalid" : "form-control"}
                      autoComplete="off"
                      placeholder="ex: Device1"
                      value={localDeviceName}
                      onChange={(e) => {
                        setLocalDeviceName(e.target.value);
                      }}
                    />
                  </div>
                  {errors?.deviceName && (
                    <div className="invalid-feedback d-block">
                      <div>{errors.deviceName?.message}</div>
                    </div>
                  )}
                </div>
                <div className="d-grid gap-2 mt-2">
                  <Button disabled={submitDisabled} variant="primary" onClick={handleActivateDevice}>
                    Register
                  </Button>
                </div>
                <div className="d-grid">{submitDisabled && <p className="text-center">Please wait...</p>}</div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ModalRegisterDevice;
